import { setupFirebaseMessaging } from './firebase/messaging';
import { registerListeners } from './chrome/listener';
import { isFullyConfigured } from './helper/authentication';
import { ensureStoresAreInit } from './storage/stores';
import { setupQApi } from './api/setupQApi';
import { loadLRUFromCache } from './storage/classificationLRU';
import { syncRules } from './actions/engineActions';
import { openOnboardingScreen } from './actions/navigationActions';
import { identifySentry, setupSentry } from './lib/vendor/sentry';
import { setupYoutubeSafeMode } from './safeSearch/youtube/youtubeRestrictedMode';
import { isSessionFirstStart, setSessionStarted } from './chrome/session';
import {
  setupSafeNetworks,
  onSafeNetworkFeatureDisabled,
  shouldDisableSafeNetworksFeature,
  canUseSafeNetworkFeature,
} from './safeNetworks';
import { setupAlarmsCallbacks } from './alarms';
import { updateProtectionPopup } from './popup/protectionPopup';
import { initializePlatformInfo } from './clientIdentification';
import { setupNavigation } from './navigation/setupNavigation';
import { setupAuthentication } from './onboarding/authentication';
import { startListeningDelegationActions } from './delegation/delegationListener';
import { setupDebugConsole } from './debug/console';
import { startReportScreenTime } from './screenTime/report';
import { isLoggedInWithDelegation } from './delegation/delegation';
import { updateDeviceInfoIfNeeded } from './device/report';
import { setupRemoteLogger } from './logging/remoteLogger';
import { ensureFcmTokenRegistered } from './firebase/tokenSync';
import { trustedTime } from './lib/dateTime';
import { captureException } from './helper/sentry';

declare const isDelegation: boolean;
declare const isDebugConsoleEnabled: boolean;

const isTestEnv = () =>
  ['test', 'testing'].includes(process.env.NODE_ENV as string);

/**
 * Initialize the extension components.
 *
 * This will run every time the service worker goes active.
 */
export const init = async () => {
  console.log('[Service Worker] Init');
  await initializeData();

  setupRemoteLogger();

  // Listen for authentication events and login when needed
  setupAuthentication();

  registerListeners();

  const isSessionStart = await isSessionFirstStart();
  if (isSessionStart) {
    await setSessionStarted();
  }

  // Start listening for debug commands
  if (isDebugConsoleEnabled) {
    setupDebugConsole();
  }

  if (!isFullyConfigured()) {
    if (!isDelegation) {
      openOnboardingScreen();
    }
    console.log('[Service Worker] Not fully configured, but init finished');
    return;
  }

  // Initialize components that require the extension to be fully configured
  await initWhenFullyConfigured(isSessionStart, isLoggedInWithDelegation());
  console.log('[Service Worker] Init when fully configured finished');
};

/**
 * Initialize storage and credentials.
 *
 * This will only initialize data once on each Service Worker run, no matter how many times it's called.
 */
const initializeData = (() => {
  let dataInitializationPromise: Promise<void> | undefined;
  return () => {
    if (dataInitializationPromise) {
      return dataInitializationPromise;
    }
    dataInitializationPromise = new Promise<void>(async resolve => {
      console.log('[Service Worker] initializeData start');
      await ensureStoresAreInit();

      // Set deviceUID and accountUID for Sentry events as soon as the storage is ready to get them
      identifySentry();

      // Fire-and-forget to avoid blocking init when Cloudflare is unavailable.
      void trustedTime.initTrustedTime();

      // Initialize platform info (arch, os) and OS version
      await initializePlatformInfo();

      // Setup QAPI if extension is configured
      if (isFullyConfigured()) {
        await setupQApi();
      }
      console.log('[Service Worker] initializeData finish');
      resolve();
    });
    return dataInitializationPromise;
  };
})();

/**
 * Initialize the components for a fully configured extension.
 *
 * This will run every time the service worker goes active and only if the extension is fully configured.
 */
export const initWhenFullyConfigured = async (
  isFirstInitInSession: boolean,
  shouldStartReportScreenTime: boolean
) => {
  console.log('[Service Worker] InitWhenFullyConfigured');

  // Start listening for HTTP requests and navigation events
  setupNavigation();

  loadLRUFromCache();
  // Sync rules but only on the first start, so we don't over fetch
  if (isFirstInitInSession) {
    console.log('[Service Worker] Syncing rules');
    await syncRules();

    // FCM token is only needed for delegation builds
    if (isDelegation) {
      try {
        console.log(
          '[Service Worker] Ensuring FCM token is registered for delegation'
        );
        await ensureFcmTokenRegistered();
      } catch (error) {
        console.error(
          '[Service Worker] Error with FCM token registration:',
          error
        );
        captureException(error as Error);
      }
    }
  }

  // Show protection state UI
  console.log('[Service Worker] Show protection state UI');
  await updateProtectionPopup();

  // This is needed to restore the protection state for delegation users who got stuck in a safe network state.
  // Ideally this should be in a proper update migration script, but we don't have that mechanism yet.
  if (shouldDisableSafeNetworksFeature()) {
    await onSafeNetworkFeatureDisabled();
    console.log('Safe Networks feature disabled for delegation!');
  } else if (canUseSafeNetworkFeature()) {
    await setupSafeNetworks(isFirstInitInSession);
  }

  await setupYoutubeSafeMode();

  // Report pending Screen Time events every minute
  if (shouldStartReportScreenTime) {
    startReportScreenTime();
  }

  // Update device details and extension version to backend, if needed
  if (isDelegation) {
    console.log('[Service Worker] Updating device info for delegation');
    updateDeviceInfoIfNeeded();
  }
};

/* Run service worker script
 *
 * @remarks
 * WARNING! Any initialization done here might be needed to be placed in the content script and custom pages as well.
 */
if (isTestEnv() === false) {
  setupSentry();
  setupAlarmsCallbacks(initializeData);

  // Start listening for delegation actions
  if (isDelegation) {
    // Chrome requires push listeners to be registered during initial script evaluation
    setupFirebaseMessaging();
    startListeningDelegationActions(initializeData);
  }

  // Finish initializing the extension asynchronously
  init();
}
